import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";

export type AppRole = "admin" | "foreman" | "worker";

export function useUserRole() {
  const { user, isLoading: authLoading } = useAuth();
  const { viewAs } = useImpersonation();
  const query = useQuery({
    queryKey: ["user-role", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id);
      if (error) throw error;
      const roles = (data ?? []).map((r) => r.role as AppRole);
      return {
        roles,
        isAdmin: roles.includes("admin"),
        isForeman: roles.includes("foreman"),
        isWorker: roles.includes("worker"),
      };
    },
  });
  const realIsAdmin = query.data?.isAdmin ?? false;
  const realIsForeman = query.data?.isForeman ?? false;
  const realIsWorker = query.data?.isWorker ?? false;
  const impersonating = realIsAdmin && !!viewAs;
  return {
    roles: query.data?.roles ?? [],
    isAdmin: impersonating ? false : realIsAdmin,
    isForeman: impersonating ? true : realIsForeman,
    isWorker: impersonating ? true : realIsWorker,
    realIsAdmin,
    realIsForeman,
    realIsWorker,
    impersonating,
    isLoading: authLoading || query.isLoading,
  };
}